import React, { useRef } from "react";
import { Divider, Typography } from "@mui/material";
import IntroductionImg3 from "../../../assets/SvgIcons/IntroductionImg3.svg";
import "../JaldiLab.css";
import {
  DashedBorderStyle,
  captionStyle,
  captionWithBorders,
  getTextContentHeading,
  getTextContentWithHeading,
  h1style,
  h2style,
  h3style,
  h4style,
  paraStyle,
  referenceHtml,
  referenceStyle,
  referenceTootKit,
  sectionStyle,
} from "./ChapterList";
import objective from "../../../assets/SvgIcons/obj.svg";
import adjacencies from "../../../assets/SvgIcons/adjacencies.svg";
import designConsideration from "../../../assets/SvgIcons/design-consideration.svg";
import femaleAblution from "../../../assets/Images/female_ablution.jpeg";
import prayerRoomImg from "../../../assets/Images/prayer_room.jpeg";
import InteriorViewImg from "../../../assets/Images/PrayerRoom Images/Interiror_view_of_prayer_room.jpg";
import CaptionWithBorders1 from "../../../components/ChapterImage/Image";
import { ImageDesc91, ImageDesc92 } from "../../../utils/tmp/ImageDescription/PrayerRoomID";

function PrayerRoom({ goToSection }) {
  const referencesRef = useRef(null);

  const handleScrollToReferences = () => {
    if (referencesRef.current) {
      referencesRef.current.scrollIntoView({
        block: "center",
        inline: "nearest",
        behavior: "smooth",
      });
    }
  };

  const reference = (_data, number) => {
    return referenceTootKit(_data, number, handleScrollToReferences);
  };

  const referenceData = [
    {
      content:
        <> ‘Guidance: Creating and managing effective multifaith and contemplation space’ <i>(Inclusive Employers)</i> </>,
      link: "https://www.southampton.ac.uk/blog/wp-content/uploads/sites/19/2018/05/Multi-Faith-Room-Guidance.pdf",
      description:
        "<https://www.southampton.ac.uk/blog/wp-content/uploads/sites/19/2018/05/Multi-Faith-Room-Guidance.pdf>",
    },
    {
      content:
        <>Diversity Matters: Multi-Faith Room Provision in the Workplace’ (<i>WuduMate,</i> 2021)</>,
      link: "https://wudumate.com/wp-content/uploads/2023/01/Diversity_Matters_JAN2023-1.pdf",
      description:
        "<https://wudumate.com/wp-content/uploads/2023/01/Diversity_Matters_JAN2023-1.pdf>",
    },
  ];

  const ObjectivesArea = {
    icon: <img src={objective} />,
    heading: "Objectives",
    data: [
      <>
        Provide prayer rooms that{" "}
        <span style={{ color: "#DC002D" }}>
          {" "}
          account for rituals across diverse religions and universally
          applicable environment conditions
        </span>
        ; and
      </>,
      <>
        Design the room to facilitate diverse user groups within a Court Complex
        to
        <span style={{ color: "#DC002D" }}>
          {" "}
          freely express their beliefs without the constraints of specific
          religious requirements.{reference(referenceData[0], 1)}
        </span>{" "}
      </>,
    ],
    isOrdered: true,
    isImage: true
  };

  const AdjacenciesData = {
    icon: <img src={adjacencies} />,
    heading: "Adjacencies",
    data: [
      <>
        The prayer room facility may be provided in a separate part of the Court
        Complex, which is removed from the major public circulation area, and is
        acoustically isolated, to prevent disturbance to other users or to Court
        proceedings. For further details of acoustics, please refer to{" "}
        <span
          style={sectionStyle}
          onClick={() => goToSection("annexes", "HeadingAnnexure5")}
        >
          Annexure 5.
        </span>{" "}
      </>,
    ],

    isOrdered: true,
    isImage: true
  };

  const DesignConsiderations = {
    icon: <img src={designConsideration} />,
    heading: "Design Considerations",
    data: [
      <>
        The layout of the room must be adaptable to various faith practices
        while enabling uninterrupted movement and universally accessible spaces.
        {reference(referenceData[1], 2)}
      </>,

      "Explicit religious imagery, colours, lighting, flooring design and materials/textures must be avoided to create a neutral ambience.",

      "Partitions/screens must be provided to ensure privacy.",

      "The prayer room must be quiet, with entry/exit points being planned in a manner ensuring minimal disruption for users of the room.",
    ],

    isOrdered: true,
    isImage: true
  };

  return (
    <div>
      <Typography>
        <h1 id="handbookPrinciple" style={h1style}>
          PART C - THE HANDBOOK
        </h1>{" "}
        <h2 id="commonAmenities" style={h2style}>
          <span style={{ fontSize: "24px" }}>3. </span>COMMON AMENITIES
        </h2>
        <h3 id="mainheading" style={h3style}>
          <span style={{ fontSize: "20px" }}>3.5. </span>Prayer Room
        </h3>
        <p style={paraStyle}>
          A multi-faith prayer room may be provided in the Court Complex to
          ensure that people of differing religious beliefs are able to use the
          space for offering prayers during Court hours. The prayer room must be
          unadorned to ensure that it may serve the needs of diverse users.
        </p>
        {getTextContentHeading(ObjectivesArea)}
        {getTextContentHeading(AdjacenciesData)}
        {getTextContentHeading(DesignConsiderations)}
        <CaptionWithBorders1
          image={[InteriorViewImg]}
          caption="Image 91: Example of a prayer room"
          creditLabel="Source"
          imageCredit="Chek Lap Kok International Airport, Hong Kong"
          zoomImgStyle={{ height: "500px" }}
          imageDescription={ImageDesc91}
        />
        <CaptionWithBorders1
          image={[femaleAblution, prayerRoomImg]}
          zoomImgStyle={{ height: "500px" }}
          caption="Image 92: Examples of Ablution for Prayer Rooms"
          creditLabel="Image Credit"
          imageCredit="369 Ochre Studio"
          imageDescription={ImageDesc92}
        />

        <h2 ref={referencesRef} id="reference" style={referenceStyle}>
          References
          <Divider />
        </h2>
        {referenceHtml(referenceData)}
      </Typography>
    </div>
  );
}

export default PrayerRoom;
